<script>
import axios from 'axios';
import { getEcho } from '@/plugins/echo';
import { nextTick } from 'vue';

export default {
  name: 'NotificationsDropdown',
  data() {
    return {
      notifications: [],
      unreadCount: 0,
      loading: false,
      handledNotificationIds: new Set(), // Prevent duplicates
    };
  },
  mounted() {
    this.fetchNotifications();
    nextTick(() => {
      this.listenForNotifications();
    });
  },
  beforeUnmount() {
    this.stopListening();
  },
  methods: {
    async fetchNotifications() {
      this.loading = true;
      try {
        const url = `${process.env.VUE_APP_AUTHDOMAIN}/api/v1/notifications`;
        const res = await axios.get(url);
        this.notifications = res.data.data;
        this.unreadCount   = res.data.unread_count;

        // Mark existing IDs as handled so we don't alert on them if Echo replays them
        if (res.data.data) {
          res.data.data.forEach(n => this.handledNotificationIds.add(n.id));
        }
      } catch (e) {
        console.warn('Failed to load notifications', e);
      } finally {
        this.loading = false;
      }
    },

    listenForNotifications() {
      console.log('[Echo] Setting up notifications listener...');
      try {
        const userId = localStorage.getItem('userid');
        if (!userId) return;

        const echoInstance = getEcho();
        if (!echoInstance) return;

        echoInstance.connector.options.auth.headers.Authorization =
                    `Bearer ${localStorage.getItem('jwt')}`;

        echoInstance
          .private(`App.Models.User.${userId}`)
          .notification((notification) => {
            console.log('[Echo] Notification received:', notification.id);

            // Prevent duplicate handling
            if (this.handledNotificationIds.has(notification.id)) {
              console.log('[Echo] Notification already handled:', notification.id);
              return;
            }
            this.handledNotificationIds.add(notification.id);

            // Prepend and cap
            this.notifications = [
              {
                id:          notification.id,
                type:        notification.data?.type        ?? null,
                message:     notification.data?.message     ?? null,
                ref_no:      notification.data?.ref_no      ?? null,
                proposal_id: notification.data?.proposal_id ?? null,
                status:      notification.data?.status      ?? null,
                actor_name:  notification.data?.actor_name  ?? null,
                url:         notification.data?.url         ?? null,
                read:        false,
                created_at:  'just now',
              },
              ...this.notifications,
            ].slice(0, 7);

            this.unreadCount += 1;
          });
      } catch (err) {
        console.warn('Echo listener could not be started:', err);
      }
    },

    stopListening() {
      const userId = localStorage.getItem('userid');
      if (userId) {
        const echoInstance = getEcho();
        if (echoInstance) {
          echoInstance.leave(`App.Models.User.${userId}`);
        }
      }
    },

    async markAllRead() {
      try {
        await axios.post(`${process.env.VUE_APP_AUTHDOMAIN}/api/v1/notifications/mark-all-read`);
        this.notifications = this.notifications.map(n => ({ ...n, read: true }));
        this.unreadCount = 0;
      } catch (e) {
        console.warn('Failed to mark notifications as read', e);
      }
    },

    async clearRead() {
      try {
        await axios.delete(`${process.env.VUE_APP_AUTHDOMAIN}/api/v1/notifications/read`);
        this.notifications = this.notifications.filter(n => !n.read);
        this.unreadCount = this.notifications.filter(n => !n.read).length;
      } catch (e) {
        console.warn('Failed to clear read notifications', e);
      }
    },

    async handleNotificationClick(n) {
      if (!n.read) {
        try {
          await axios.post(`${process.env.VUE_APP_AUTHDOMAIN}/api/v1/notifications/${n.id}/mark-as-read`);
          n.read = true;
          if (this.unreadCount > 0) this.unreadCount--;
        } catch (e) {
          console.warn('Failed to mark notification as read', e);
        }
      }

      if (n.url) {
        this.$router.push(n.url);
      }
    },

    statusIcon(status) {
      const map = {
        'Draft':              'bx bx-edit-alt text-dark',
        'Submitted to Client':'bx bx-send text-dark',
        'Accepted':           'bx bx-check-circle text-aai',
        'Rejected':           'bx bx-x-circle text-aai',
        'Awarded':            'bx bxs-award text-aai',
      };
      return map[status] || 'bx bx-bell text-muted';
    },
  },
  computed: {
    isNotificationsPage() {
      return this.$route.name === 'Notifications';
    },
  },
};
</script>

<template>
    <div v-if="!isNotificationsPage" class="dropdown topbar-head-dropdown ms-1 header-item">
        <!-- Bell button with badge -->
        <button
            type="button"
            class="btn btn-icon btn-topbar btn-ghost-aai rounded-circle position-relative"
            id="page-header-notifications-dropdown"
            data-bs-toggle="dropdown"
            aria-haspopup="true"
            aria-expanded="false"
        >
            <i class="bx bx-bell fs-22"></i>
            <span
                v-if="unreadCount > 0"
                class="position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger fs-10"
                style="font-size: 0.65rem; min-width: 18px;"
            >
                {{ unreadCount > 99 ? '99+' : unreadCount }}
                <span class="visually-hidden">unread notifications</span>
            </span>
        </button>

        <!-- Dropdown panel -->
        <div
            class="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0"
            aria-labelledby="page-header-notifications-dropdown"
            style="min-width: 360px;"
        >
            <!-- Header -->
            <div class="p-3 border-bottom d-flex align-items-center justify-content-between">
                <div>
                    <h6 class="mb-0 fw-semibold">Notifications</h6>
                    <small class="text-muted">{{ unreadCount }} unread</small>
                </div>
                <div class="d-flex gap-2">
                    <button
                        v-if="unreadCount > 0"
                        class="btn btn-sm btn-soft-aai"
                        @click.stop="markAllRead"
                        title="Mark all as read"
                    >
                        <i class="bx bx-check-double me-1"></i> Mark all read
                    </button>
                    <button
                        class="btn btn-sm btn-soft-dark"
                        @click.stop="clearRead"
                        title="Clear read notifications"
                    >
                        <i class="bx bx-trash"></i>
                    </button>
                </div>
            </div>

            <!-- Loading -->
            <div v-if="loading" class="text-center py-4">
                <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
                <p class="mt-2 mb-0 text-muted small">Loading…</p>
            </div>

            <!-- Empty state -->
            <div v-else-if="notifications.length === 0" class="text-center py-5 px-3">
                <i class="bx bx-bell-off fs-1 text-muted"></i>
                <p class="mt-2 mb-0 text-muted">No notifications yet</p>
            </div>

            <!-- Notification list -->
            <ul v-else class="list-unstyled mb-0">
                <li
                    v-for="n in notifications"
                    :key="n.id"
                    class="d-flex align-items-start gap-3 px-3 py-2 border-bottom notification-item"
                    :class="{ 'unread-gray': !n.read, 'cursor-pointer': !!n.url }"
                    @click="handleNotificationClick(n)"
                >
                    <div class="flex-shrink-0 mt-1">
                        <i :class="statusIcon(n.status)" style="font-size: 1.4rem;"></i>
                    </div>
                    <div class="flex-grow-1 overflow-hidden">
                        <p class="mb-0 text-truncate small fw-medium" :title="n.message">
                            {{ n.message }}
                        </p>
                        <small class="text-muted">{{ n.created_at }}</small>
                    </div>
                    <span v-if="!n.read" class="flex-shrink-0 mt-1">
                        <span class="badge bg-aai rounded-pill" style="width: 8px; height: 8px; padding: 0; display: inline-block;"></span>
                    </span>
                </li>
            </ul>

            <!-- View All Footer -->
            <div class="p-2 border-top text-center">
                <router-link to="/notifications" class="btn btn-link btn-sm text-aai text-decoration-none shadow-none fw-bold">
                    View All <i class="ri-arrow-right-line align-middle"></i>
                </router-link>
            </div>
        </div>
    </div>
</template>

<style scoped>
.notification-item:hover {
    background-color: #f8f9fa;
}
.unread-gray {
    background-color: rgba(0, 0, 0, 0.02);
}
.cursor-pointer {
    cursor: pointer;
}
</style>